import { useObservable } from '@/hooks/rx-hooks'
import {
  Children,
  cloneElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { animationFrameScheduler, type Observable } from 'rxjs'
import { observeOn } from 'rxjs/operators'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import styles from './Section.module.css'
import { Tooltip } from '@/components/Tooltip'

interface SectionProps {
  children?: ReactNode
  collapsible?: boolean
  collapsed?: boolean
  onToggleCollapse?: () => unknown
  scrollMargin?: number
  scrollIntoView$?: Observable<unknown>
  ref?: Ref<HTMLDivElement>
  testId?: string
}

/**
 * A titled block of content that can collapse; passes its collapse state to its header.
 */
export function Section({
  collapsible,
  collapsed,
  children,
  onToggleCollapse,
  scrollMargin,
  scrollIntoView$,
  ref: refProp,
  testId,
}: SectionProps) {
  const isCollapsed = collapsible && collapsed
  const rootRef = useRef<HTMLDivElement | null>(null)
  const ref = useComposedRefs(refProp, rootRef)
  useObservable(
    useMemo(
      () => scrollIntoView$?.pipe(observeOn(animationFrameScheduler)),
      [scrollIntoView$],
    ),
    () =>
      rootRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      }),
  )
  return (
    <div
      className={cx(styles.root, isCollapsed && styles.collapsed)}
      ref={ref}
      style={{ scrollMarginTop: scrollMargin }}
      data-test={testId}
    >
      {Children.map(children, (c) =>
        cloneElement(
          c as ReactElement<
            Pick<
              SectionHeaderProps,
              'collapsible' | 'collapsed' | 'onToggleCollapse'
            >
          >,
          {
            collapsible,
            collapsed,
            onToggleCollapse,
          },
        ),
      )}
    </div>
  )
}

interface SectionHeaderProps extends Pick<
  SectionProps,
  'collapsed' | 'collapsible'
> {
  children?: ReactNode
  actions?: ReactNode
  borderless?: boolean
  onToggleCollapse?: () => unknown
}

/**
 * The header of a {@link Section}, with the collapse toggle and actions.
 */
export function SectionHeader({
  collapsible,
  collapsed,
  borderless,
  onToggleCollapse,
  children,
  actions,
}: SectionHeaderProps) {
  const toggleCollapse = useCallback(
    () => onToggleCollapse?.(),
    [onToggleCollapse],
  )
  return (
    <div className={cx(styles.header, borderless && styles.borderless)}>
      {collapsible && (
        <div className={styles.left}>
          <RawButton
            className={cx(
              styles.btnCollapseToggle,
              collapsed && styles.collapsed,
            )}
            onPress={toggleCollapse}
          >
            <Icon name="arrow" />
          </RawButton>
        </div>
      )}
      <div className={styles.headerContent}>{children}</div>
      {actions && <div className={styles.right}>{actions}</div>}
    </div>
  )
}

interface SectionTitleProps {
  children?: ReactNode
}

/**
 * The section's heading.
 */
export function SectionTitle({ children }: SectionTitleProps) {
  return <p className={styles.title}>{children}</p>
}

interface SectionHeaderActionProps {
  children?: ReactNode
  isDisabled?: boolean
  title?: string
  active?: boolean
  onPress?: () => unknown
  tooltip?: string
  compact?: boolean
}

/**
 * A compact button among the section header's actions.
 */
export function SectionHeaderAction({
  children,
  onPress,
  isDisabled,
  title,
  active,
  tooltip,
  compact,
}: SectionHeaderActionProps) {
  const element = (
    <RawButton
      title={title}
      isDisabled={isDisabled}
      onPress={onPress}
      className={cx(
        styles.headerAction,
        compact && styles.compact,
        isDisabled && styles.disabled,
        active && styles.active,
      )}
    >
      {children}
    </RawButton>
  )
  return tooltip ? <Tooltip content={tooltip}>{element}</Tooltip> : element
}

interface SectionTitleSuffixProps {
  children?: ReactNode
}

/**
 * Secondary text after the section's title.
 */
export function SectionTitleSuffix({ children }: SectionTitleSuffixProps) {
  return <span className={styles.titleSuffix}>{children}</span>
}

interface SectionContentProps {
  children?: ReactNode
  collapsed?: boolean
}

/**
 * The section's body, hidden while collapsed.
 */
export function SectionContent({ children, collapsed }: SectionContentProps) {
  if (collapsed) {
    return null
  }

  return <div>{children}</div>
}

/**
 * The collapse state and actions that {@link useSection} returns.
 */
export interface SectionController {
  collapsed: boolean
  collapsible: boolean
  onToggleCollapse: () => unknown
  expand: () => unknown
  collapse: () => unknown
}

/**
 * Manages the collapse state of a collapsible {@link Section}.
 */
export function useSection(collapseInitially?: boolean): SectionController {
  const [collapsed, setCollapsed] = useState(collapseInitially || false)
  const onToggleCollapse = useCallback(() => setCollapsed((c) => !c), [])
  const collapse = useCallback(() => setCollapsed(true), [])
  const expand = useCallback(() => setCollapsed(false), [])
  return useMemo(
    () => ({
      collapsed,
      onToggleCollapse,
      collapsible: true,
      collapse,
      expand,
    }),
    [collapsed, onToggleCollapse, collapse, expand],
  )
}
